// biome-ignore-all lint/security/noDangerouslySetInnerHtml: the preview renders admin-authored template text through sanitizeRichText (whitelist: b/u/span[font-size]/br), never raw user input.
// =============================================================================
// NextStepsAdmin — edit "next steps" action-sheet templates
// =============================================================================
// Renders: a list of next-step snippets used when staff confirm approvals.
//          Stored as a JSONB blob in `portal_config`. Seeded from
//          NEXT_STEPS_DEFAULTS (services/approvals). Editing happens in a modal
//          rich-text editor (Bold / Underline / Font-size — RichTextEditorModal);
//          the stored value is the sanitised HTML subset (services/richText), so
//          old plain-text templates keep working unchanged.
// Props:
//   user - admin user (for audit logging on save)
// =============================================================================
import React from "react";
import { deleteConfig, upsertConfig } from "../../data/portalConfig";
import { invalidateNextStepsCache, useNextStepsTemplates } from "../../hooks/useNextStepsTemplates";
import { NEXT_STEPS_DEFAULTS } from "../../services/approvals";
import { confirmDialog } from "../../services/dialogService";
import { richTextDisplayHtml } from "../../services/richText";
import { logAudit } from "../../utils/auditLog";
import { RichTextEditorModal } from "./RichTextEditorModal.jsx";

export function NextStepsAdmin({ user }) {
  const _nst = useNextStepsTemplates(NEXT_STEPS_DEFAULTS);
  const templates = _nst.templates;
  const isLoaded = _nst.isLoaded;
  const [editing, setEditing] = React.useState(null); // template id being edited
  const [saving, setSaving] = React.useState(false);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [localTemplates, setLocalTemplates] = React.useState([]);

  React.useEffect(
    function () {
      if (isLoaded)
        setLocalTemplates(
          templates.map(function (t) {
            return Object.assign({}, t);
          })
        );
    },
    [isLoaded, templates]
  );

  function saveOne(html) {
    const editedId = editing;
    const updated = localTemplates.map(function (t) {
      if (t.id === editedId) return Object.assign({}, t, { text: html });
      return t;
    });
    setLocalTemplates(updated);
    setSaving(true);
    upsertConfig("next_steps_templates", updated).then(function (r) {
      setSaving(false);
      if (r.error) {
        setSavedMsg(`Save failed: ${r.error.message}`);
      } else {
        setSavedMsg("Saved");
        invalidateNextStepsCache();
        logAudit("next_steps_template_updated", { id: editedId, by: user?.email || "" });
        setEditing(null);
      }
      setTimeout(function () {
        setSavedMsg("");
      }, 3000);
    });
  }

  async function resetToDefaults() {
    if (
      !(await confirmDialog({
        title: "Reset templates?",
        message: "Reset all action sheet templates to their defaults? This cannot be undone.",
        confirmLabel: "Reset",
        danger: true,
      }))
    )
      return;
    setLocalTemplates(
      NEXT_STEPS_DEFAULTS.map(function (t) {
        return Object.assign({}, t);
      })
    );
    setSaving(true);
    deleteConfig("next_steps_templates").then(function () {
      setSaving(false);
      setSavedMsg("Reset to defaults");
      invalidateNextStepsCache();
      logAudit("next_steps_templates_reset", { by: user?.email || "" });
      setTimeout(function () {
        setSavedMsg("");
      }, 3000);
    });
  }

  if (!isLoaded) return <div className="text-zephyr-muted p-5">Loading...</div>;

  const editingTemplate = localTemplates.find(function (t) {
    return t.id === editing;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div>
          <h2 className="m-0 text-[18px] text-zephyr-text">Action Sheet Templates</h2>
          <p className="mt-1 mb-0 text-[13px] text-zephyr-muted">
            These instructions appear on the DFV Service's action sheets after items are approved.
            Click Edit to change the text and formatting (bold, underline, font size).
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={resetToDefaults}
            className="bg-transparent border border-zephyr-border rounded-[6px] py-[6px] px-[14px] text-xs cursor-pointer text-zephyr-muted"
            style={{ fontFamily: "inherit" }}
          >
            Reset to Defaults
          </button>
        </div>
      </div>
      {savedMsg && <div className="text-[#2E7D32] font-semibold text-[13px] mb-3">{savedMsg}</div>}
      <div className="bg-zephyr-off-white rounded-[8px] py-[10px] px-[14px] mb-4 text-xs text-zephyr-muted">
        <strong>Available variables:</strong> {"{{supplier}}"} — supplier name (uniforms only)
        &nbsp;·&nbsp;
        {"{{refNo}}"} — request reference number
      </div>
      {localTemplates.map(function (t) {
        return (
          <div
            key={t.id}
            className="mb-3 border border-zephyr-border rounded-[10px] overflow-hidden"
          >
            <div className="bg-zephyr-teal-dark text-white py-[10px] px-4 flex justify-between items-center">
              <div>
                <div className="font-bold text-[14px]">{t.title}</div>
                <div className="text-[11px] opacity-70">
                  {t.category}
                  {t.match?.feeType ? ` · ${t.match.feeType}` : ""}
                  {t.match?.supplier ? ` · ${t.match.supplier}` : ""}
                  {t.match?.supplierType ? ` · ${t.match.supplierType}` : ""}
                  {t.match?.state ? ` · ${t.match.state}` : ""}
                </div>
              </div>
              <button
                onClick={function () {
                  setEditing(t.id);
                }}
                className="bg-white/15 text-white border border-white/30 rounded-[6px] py-1 px-3 text-[11px] font-semibold cursor-pointer"
                style={{ fontFamily: "inherit" }}
              >
                Edit
              </button>
            </div>
            <div className="p-[14px]">
              <div
                className="text-xs text-zephyr-text leading-[1.6]"
                style={{ fontFamily: "inherit", whiteSpace: "pre-wrap" }}
                dangerouslySetInnerHTML={{ __html: richTextDisplayHtml(t.text || "") }}
              />
            </div>
          </div>
        );
      })}

      {editingTemplate && (
        <RichTextEditorModal
          title={`Edit — ${editingTemplate.title}`}
          initialHtml={editingTemplate.text}
          hint={
            <span>
              <strong>Variables:</strong> {"{{supplier}}"} (uniforms) · {"{{refNo}}"} — type them as
              plain text where you want them.
            </span>
          }
          onSave={saveOne}
          onCancel={function () {
            setEditing(null);
          }}
        />
      )}
      {saving && <div className="text-[12px] text-zephyr-muted mt-1">Saving…</div>}
    </div>
  );
}
